// D1: a token for this tab's signed-in session, stamped on every browser
// history entry HistoryBridge writes ({ memPath, sid, ... }). Entries from an
// earlier session (the previous user on a shared tab, the company before a
// switch) carry another token, so a reload (bootEntry.ts restoredEntry) and
// Back (HistoryBridge's popstate) leave them alone. Sign-in and company switch
// start a new session; sign-out and idle logout end it. Kept in sessionStorage
// (per tab, survives F5), with an in-memory copy for a browser that blocks
// storage. No imports: bootEntry.ts, reachable from the Sales rep bundle, uses it.
const KEY = 'mrp.history.sid';
// Who the session belongs to (`${user id}:${company id}`), so a tab whose
// cookie now speaks for someone else (a sign-in or company switch in another
// tab) drops it at its next /auth/me (claimHistorySession).
const OWNER_KEY = 'mrp.history.owner';
let memo: string | null = null;
let memoOwner: string | null = null;

// Saved list state (shared/hooks/listSession.ts, nav-15) belongs to one session:
// starting or ending a session forgets every list the last one saved.
function clearListSessions(): void {
  try {
    const ss = window.sessionStorage;
    for (let i = ss.length - 1; i >= 0; i--) {
      const k = ss.key(i);
      if (k && k.startsWith('list:')) ss.removeItem(k);
    }
  } catch { /* storage blocked: nothing was saved */ }
}

export function historySid(): string | null {
  try {
    return window.sessionStorage.getItem(KEY);
  } catch {
    return memo;
  }
}

export function historyOwner(): string | null {
  try {
    return window.sessionStorage.getItem(OWNER_KEY);
  } catch {
    return memoOwner;
  }
}

export function startHistorySession(owner?: string): string {
  const sid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  memo = sid;
  memoOwner = owner ?? null;
  clearListSessions();
  try {
    window.sessionStorage.setItem(KEY, sid);
    if (owner) window.sessionStorage.setItem(OWNER_KEY, owner);
    else window.sessionStorage.removeItem(OWNER_KEY);
  } catch { /* memory copy only */ }
  return sid;
}

// The signed-in user and company as /auth/me reports them. Keeps this tab's
// session when it is theirs; otherwise (another owner, or none recorded)
// starts a new one, forgetting the saved lists, and returns true: the page
// the tab booted at (restoredEntry) was not theirs and must be dropped. With
// no session yet it starts one for them, and there is nothing to drop.
export function claimHistorySession(owner: string): boolean {
  const sid = historySid();
  if (sid && historyOwner() === owner) return false;
  startHistorySession(owner);
  return !!sid;
}

export function ensureHistorySid(): string {
  return historySid() ?? startHistorySession();
}

// Ends the session and clears the current entry's page, so a reload of the
// sign-in screen does not reopen the last record. Call it before navigating away.
export function endHistorySession(): void {
  memo = null;
  memoOwner = null;
  clearListSessions();
  try { window.sessionStorage.removeItem(KEY); window.sessionStorage.removeItem(OWNER_KEY); } catch { /* memory copy only */ }
  window.history.replaceState(null, '', '/');
}
